import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2, Activity, Gauge, Bot, Camera, LineChart, Cloud,
  Bell, SlidersHorizontal, Wifi, Cpu, Zap, Shield, ArrowRight,
  Database, BarChart3, Mic, Images, Settings2, Wind, Thermometer, Monitor, Video,
} from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { useLightbox } from "../components/Lightbox";

const fade = { hidden: { opacity: 0, y: 20 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.45 } }) };

const FEATURE_GROUPS = [
  {
    icon: Activity,
    title: "3-Phase Energy Metering",
    color: "#00FF66",
    items: [
      "Per-phase voltage, current & power (PZEM-004T × 3)",
      "Power factor & frequency on every line",
      "Cumulative kWh energy logging per phase",
      "Animated gauges updated at 2 Hz",
      "ESP32 reads all three phases simultaneously over Modbus-RTU",
    ],
  },
  {
    icon: Thermometer,
    title: "Temperature & Thermal Control",
    color: "#FF6B35",
    items: [
      "DS18B20 OneWire temperature probe (GPIO 5, ±0.5°C)",
      "TIP120 NPN Darlington - PWM fan speed control (GPIO 27)",
      "4 auto-thresholds: 30% / 60% / 100% / critical fan ramp",
      "Manual override or full AUTO temp-control mode",
      "Temperature shown on dashboard, LCD, gauges, and AI chat",
      "Critical temperature toast alert (configurable in Personalize)",
    ],
  },
  {
    icon: Wind,
    title: "Air Quality Monitoring",
    color: "#00CCAA",
    items: [
      "MQ-135 gas sensor - CO₂, NH₃ & smoke detection (GPIO 34)",
      "Voltage divider: R1 = 4.7 kΩ + R2 = 10 kΩ (5V → 3.3V safe)",
      "GOOD / POOR / DANGER classification at 12-bit ADC",
      "Live ppm readout on dashboard stat card and LCD",
      "Buzzer DANGER alert + dashboard toast notification",
      "AI assistant context-aware of live air quality state",
    ],
  },
  {
    icon: Monitor,
    title: "20×4 LCD Display",
    color: "#FFD700",
    items: [
      "HD44780-compatible 20×4 character LCD (I²C via PCF8574A)",
      "Row 0: IP address or WiFi reconnecting status",
      "Row 1: temperature + fan speed (mirrors firmware exactly)",
      "Row 2: PIR motion state + WiFi status",
      "Row 3: rotates every 5 s - L1/L2/L3 power, total, gas ppm",
      "Web dashboard LCD mirrors the physical display in real time",
    ],
  },
  {
    icon: Bell,
    title: "Alerts & Notifications",
    color: "#FF4444",
    items: [
      "Temperature critical alert (configurable threshold)",
      "Air quality DANGER alert - MQ-135 gas level",
      "Power overload alert (> 4.5 kW default)",
      "MQTT disconnect toast notification",
      "5-minute cooldown per alert type (no spam)",
      "Active buzzer - 1–5 beeps or continuous STOP command",
    ],
  },
  {
    icon: SlidersHorizontal,
    title: "Remote Device Control",
    color: "#FFCC00",
    items: [
      "Fan: OFF / 30% / 60% / 100% / AUTO via TIP120 PWM (GPIO 27)",
      "Enclosure light PWM (GPIO 26) & light hub (GPIO 32, TIP120 PWM)",
      "Buzzer beep count 1–5 or STOP (GPIO 25)",
      "System standby / wake & emergency ALL-OFF command",
      "NEC IR remote as physical hardware override",
      "All commands published via MQTT campus/energy/cmd",
    ],
  },
  {
    icon: Bot,
    title: "AI Energy Assistant",
    color: "#3B9DFF",
    items: [
      "Gemini 2.5 Flash with live MQTT sensor context (incl. gas)",
      "Key rotation + DeepSeek fallback (zero downtime)",
      "Slash commands - /fan auto, /light on, /gas check, /cam snap…",
      "[CTRL:device=action] auto-execution tokens",
      "Voice call + continuous speech recognition",
      "Video call + Gemini Vision live frame analysis",
      "Multi-provider TTS: ElevenLabs → Azure → StreamElements → Browser",
    ],
  },
  {
    icon: Video,
    title: "AI Face Call - RoboFace Live",
    color: "#00E5FF",
    items: [
      "Animated SVG robot face (Cortex) with 13 expression states",
      "Real-time facial expression mirroring via Gemini Vision",
      "Voice conversations - Web Speech API with silence detection",
      "Interactive teaching - ask any topic, get story-driven lessons",
      "Idle personality - blinks, winks, eye-rolls after silence",
      "Side panel with live chat history & whiteboard tab",
      "Camera feed with face bounding box detection overlay",
    ],
  },
  {
    icon: Camera,
    title: "Camera System",
    color: "#FF6BD6",
    items: [
      "TTGO T-Camera (OV2640) on separate ESP32",
      "MQTT-based live JPEG streaming to dashboard",
      "Manual snapshots via dashboard button or IR remote",
      "PIR-triggered auto-snapshots (30 s cooldown, AM312)",
      "Rapid snapshot mode (3 / 5 / 10 / 30 / 60 s interval)",
      "Auto-flash: main LED fires during each snap",
      "Browser-stored snapshot gallery with download",
    ],
  },
  {
    icon: LineChart,
    title: "Cloud Analytics",
    color: "#9D7BFF",
    items: [
      "Grafana Cloud time-series dashboards",
      "ThingSpeak dual-channel (Ch-A: V/I/W, Ch-B: PF/Hz/kWh)",
      "React historical charts - 6 metrics, 3 phases each",
      "Google Sheets export via Apps Script",
      "Auto-refresh every 15 s from ESP32",
    ],
  },
  {
    icon: Cloud,
    title: "Cloud Infrastructure",
    color: "#6699FF",
    items: [
      "HiveMQ Cloud MQTT broker (WSS port 8884, TLS)",
      "Cloudflare Pages - global CDN, zero cold-start",
      "Firebase Authentication (email/password)",
      "ThingSpeak + Grafana Cloud analytics layers",
      "React 18 + Tailwind CSS + Recharts + Framer Motion",
    ],
  },
];

const HW_IMAGES = [
  ["/assets/hw-front.jpeg",    "Front Panel",        "LCD · PIR · Camera · Light hub"],
  ["/assets/hw-internal.jpeg", "Inside the Build",   "ESP32 · PZEM × 3 · wiring"],
  ["/assets/hw-camera.jpeg",   "Camera & Flash",     "TTGO T-Camera · Light hub flash"],
  ["/assets/hw-fan.jpeg",      "Rear Cooling",       "80mm fan · 9V & 12V inputs"],
  ["/assets/hw-panel.png",     "Field Installation", "Wired into live 3-phase panel"],
  ["/assets/hw-lcd-top.jpeg",  "20×4 LCD Top",       "Live V · P · Temp readout"],
  ["/assets/kit-flatlay.png",  "Component Flatlay",  "All parts before assembly"],
  ["/assets/hw-base.jpeg",     "Ventilated Base",    "Airflow grille · rubber feet"],
];

const ARCH_IMAGES = [
  ["/assets/arch-a.png",        "System Architecture A",   "Full end-to-end data flow"],
  ["/assets/arch-b.png",        "System Architecture B",   "MQTT topics & ESP32 state machine"],
  ["/assets/arch-c.png",        "System Architecture C",   "Cloud layer - HiveMQ · Firebase · Cloudflare"],
  ["/assets/diag-block.png",    "Block Diagram",           "Hardware block diagram"],
  ["/assets/diag-mqtt.png",     "MQTT Topology",           "Broker · publisher · subscriber flow"],
  ["/assets/diag-usecase.png",  "Use Case Diagram",        "Actor interactions & system boundary"],
];

const DIAG_IMAGES = [
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 10_50_26 PM.png", "System Overview Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 10_51_06 PM.png", "Hardware Integration Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 10_52_35 PM.png", "Data Pipeline Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 10_56_45 PM.png", "MQTT Flow Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 10_57_09 PM.png", "Sensor Wiring Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_03_36 PM.png", "ESP32 Pin Mapping"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_03_48 PM.png", "PZEM-004T Wiring"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_05_59 PM.png", "Power Supply Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_06_07 PM.png", "Camera Module Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_06_14 PM.png", "Cloud Architecture"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_09_04 PM.png", "Dashboard UI Flow"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_12_33 PM.png", "Alert System Flow"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_13_21 PM.png", "Personalize Logic"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_17_13 PM.png", "Scheduler Diagram"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_20_12 PM.png", "AI Assistant Flow"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_20_33 PM.png", "TTS Provider Chain"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_40_01 PM.png", "Security Architecture"],
  ["/assets/diagrams/ChatGPT Image Jun 17, 2026, 11_44_31 PM.png", "Deployment Pipeline"],
];

const BOM_IMAGES = [
  ["/assets/bom-shopping.jpeg",   "Shopping List",       "Materials, glue & local suppliers"],
  ["/assets/bom-tools.png",       "Tools & Equipment",   "Pricing, quantity & purpose"],
  ["/assets/bom-overview.png",    "Components Overview", "Have · source · still missing"],
  ["/assets/bom-cost.jpeg",       "Cost Breakdown",      "Total build cost in ZAR"],
  ["/assets/doc-procurement.jpeg","Procurement Docs",    "Supplier invoices & receipts"],
];

const TECH_STACK = [
  { label: "ESP32-WROOM-32U",     desc: "Dual-core Xtensa LX6 - WiFi, MQTT, UART, IR, PWM" },
  { label: "PZEM-004T × 3",       desc: "Per-phase V / I / W / PF / Hz / kWh via Modbus-RTU" },
  { label: "DS18B20",             desc: "OneWire temperature probe - GPIO 5, ±0.5°C, 4.7 kΩ pull-up" },
  { label: "MQ-135 Gas Sensor",   desc: "CO₂/NH₃/smoke - GPIO 34, voltage divider, GOOD/POOR/DANGER" },
  { label: "TIP120 Transistor",   desc: "NPN Darlington - PWM fan speed control, GPIO 27" },
  { label: "20×4 I²C LCD",        desc: "HD44780 + PCF8574A backpack - real-time telemetry display" },
  { label: "5V Active Buzzer",    desc: "Alert beeps - temperature / gas / power / system events" },
  { label: "AM312 PIR",           desc: "Motion detection - auto-triggers enclosure light & camera" },
  { label: "TTGO T-Camera",       desc: "OV2640 · live JPEG streaming over MQTT · separate ESP32" },
  { label: "HiveMQ Cloud",        desc: "Managed MQTT broker · WSS TLS · 2 Hz telemetry publishes" },
  { label: "Gemini 2.5 Flash",    desc: "AI assistant with key rotation + DeepSeek fallback" },
  { label: "Firebase Auth",       desc: "Email/password authentication for dashboard access" },
  { label: "Cloudflare Pages",    desc: "Global CDN deployment - zero cold-start, free tier" },
  { label: "React 18 + Tailwind", desc: "SPA dashboard - dark/light theme, fully responsive" },
  { label: "ThingSpeak",          desc: "Dual IoT channels for V/I/W/PF/Hz/kWh history" },
  { label: "Grafana Cloud",       desc: "Professional time-series dashboards & alerting" },
];

export default function Features() {
  const hw   = useLightbox();
  const arch = useLightbox();
  const diag = useLightbox();
  const bom  = useLightbox();

  const hwImages   = HW_IMAGES.map(([src, title, desc]) => ({ src, title, desc }));
  const archImages = ARCH_IMAGES.map(([src, title, desc]) => ({ src, title, desc }));
  const diagImages = DIAG_IMAGES.map(([src, title]) => ({ src, title }));
  const bomImages  = BOM_IMAGES.map(([src, title, desc]) => ({ src, title, desc }));

  return (
    <PublicLayout>

      {/* ── OVERVIEW VIDEO ── */}
      <section className="border-b border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Project Overview</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Watch the system in action</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg">A guided walkthrough of the Campus Energy Monitor - from hardware assembly to live dashboard.</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[var(--bd)] shadow-2xl aspect-video">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/WO-YT1pTPYM"
              title="Campus Energy Monitor - Project Overview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* ── FEATURE GROUPS ── */}
      <section className="border-b border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Capabilities</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Everything an energy operator needs</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {FEATURE_GROUPS.map(({ icon: Icon, title, color, items }, i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="p-6 rounded-xl bg-[var(--surface)] border border-[var(--bd)] hover:border-[var(--bd-strong)] transition-colors">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: color + "18", color }}>
                  <Icon size={22} />
                </div>
                <h3 className="font-heading font-bold text-lg mb-3">{title}</h3>
                <ul className="space-y-2">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-[var(--t2)]">
                      <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color }} /> {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HARDWARE GALLERY ── */}
      {hw.node}
      <section className="border-t border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Hardware</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Engineered enclosure - v1.0</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg leading-relaxed">A custom-machined housing packing the ESP32, three PZEM-004T meters, DS18B20 probe, TTGO T-Camera, PIR, buzzer and active cooling - all wired and built in Cape Town.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {HW_IMAGES.map(([src, title, desc], i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 8} variants={fade}
                className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer"
                onClick={() => hw.open(hwImages, i)}>
                <div className="aspect-square overflow-hidden bg-black/30">
                  <img src={src} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-3">
                  <h3 className="font-heading font-bold text-sm sm:text-base">{title}</h3>
                  <p className="text-xs sm:text-sm text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SYSTEM ARCHITECTURE ── */}
      {arch.node}
      <section className="border-t border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Architecture</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">System design & data flow</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg">Full end-to-end architecture - hardware sensors, MQTT broker, React frontend and cloud analytics layers.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ARCH_IMAGES.map(([src, title, desc], i) => (
              <motion.div key={title}
                initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer"
                onClick={() => arch.open(archImages, i)}>
                <div className="aspect-[4/3] overflow-hidden bg-[var(--surface2)]">
                  <img src={src} alt={title} className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                </div>
                <div className="p-4 flex items-center justify-between gap-2">
                  <div>
                    <h3 className="font-heading font-bold text-base">{title}</h3>
                    <p className="text-sm text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
                  </div>
                  <ArrowRight size={14} className="text-[var(--accent)] shrink-0 -rotate-45 group-hover:rotate-0 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECH STACK ── */}
      <section className="border-t border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Tech Stack</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Built on proven technology</h2>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {TECH_STACK.map(({ label }) => (
              <span key={label} className="px-4 py-2 rounded-full text-sm font-mono bg-[var(--surface)] border border-[var(--bd)] text-[var(--t2)]">{label}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOM & BUILD DOCS ── */}
      {bom.node}
      <section className="border-t border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Build Documentation</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Bill of materials & cost sheets</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg">Sourced locally in Cape Town - click any sheet to view full size.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BOM_IMAGES.map(([src, title, desc], i) => (
              <motion.div key={title}
                initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer"
                onClick={() => bom.open(bomImages, i)}>
                <div className="aspect-[4/3] overflow-hidden bg-white">
                  <img src={src} alt={title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                </div>
                <div className="p-4 flex items-center justify-between gap-2">
                  <div>
                    <h4 className="font-heading font-bold text-base">{title}</h4>
                    <p className="text-sm text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
                  </div>
                  <ArrowRight size={14} className="text-[var(--accent)] shrink-0 -rotate-45 group-hover:rotate-0 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="border-t border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Ready to explore the live system?</h2>
          <p className="text-[var(--t2)] mt-3 text-base sm:text-lg">Create an account and open the real-time dashboard in seconds.</p>
          <div className="flex justify-center gap-3 flex-wrap mt-8">
            <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[var(--accent)] text-black font-semibold active:scale-95 transition-transform">
              <Zap size={18} /> Launch Dashboard
            </Link>
            <Link to="/about" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md border border-[var(--bd)] font-semibold hover:border-[var(--bd-strong)] transition-colors">
              Meet the Engineer <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
}
